/**
 * intelligenceThreads.ts — typed wire contracts for durable Ask Phibian
 * conversations (API domain: `apps/api/src/domains/intelligence/threads/**`).
 *
 * Every schema here re-declares a shape the API already serves. The API
 * package is deliberately NOT imported — the same rule intelligence.ts states:
 * the admin UI must validate and render these shapes without pulling server
 * code, and the two declarations must drift LOUDLY rather than silently when
 * the domain evolves.
 *
 * THE PRIVACY MODEL, so no caller of this module has to guess:
 *
 *   A conversation belongs to (organization, owner user). Every route below is
 *   scoped to the authenticated caller by the server, which re-derives that
 *   from the session on every request. There is deliberately NO userId
 *   parameter anywhere in this file — not optional, ABSENT — because there is
 *   no request a client could make about somebody else's conversations. A
 *   thread that is a colleague's and a thread that never existed are the same
 *   404 with the same body; a screen must not try to distinguish them, because
 *   the server will not.
 *
 *   Pins, archives and titles are therefore per-person by construction, not by
 *   a filter: there is one owner, so there is one pin.
 *
 * WHAT IS STORED: only what the user saw — their message and the final answer,
 * plus the evidence and typed result objects the surface rendered beside it.
 * Never hidden model reasoning. `evidence` and `visualization` are typed here
 * as opaque `unknown` on purpose: their shapes belong to `intelligence.ts`
 * (`turnResponseSchema.evidence` and `intelligenceResultSchema`), and
 * re-declaring them here would create a THIRD copy to drift. A caller that
 * wants to render them parses them with those schemas, which is also the
 * honest thing to do with a payload written by an earlier version of the
 * server.
 *
 * Sources of truth in the API domain:
 *   - routes/paths → `domains/intelligence/threads/routes.ts`
 *   - view shapes  → `domains/intelligence/threads/service.ts`
 *   - bounds       → `domains/intelligence/threads/schemas.ts`
 *   - snippets     → `domains/messaging/search.ts` (shared planner)
 */
import { z } from 'zod';
import { del, get, patch, post } from './transport.js';
// ---------------------------------------------------------------------------
// Bounds — mirrored from the server's schemas so a screen can validate before
// the wire, and a rejected value is attributable here rather than as an
// anonymous server 400.
// ---------------------------------------------------------------------------
export const THREAD_TITLE_MAX = 200;
export const THREAD_LIST_MAX_LIMIT = 50;
export const THREAD_MESSAGE_PAGE_MAX_LIMIT = 200;
export const THREAD_SEARCH_MAX_LIMIT = 25;
/** Shared with messaging search — the same planner serves both. */
export const THREAD_SEARCH_MIN_QUERY = 2;
export const THREAD_SEARCH_MAX_QUERY = 200;
// ---------------------------------------------------------------------------
// Views
// ---------------------------------------------------------------------------
/**
 * One conversation in a list.
 *
 * TIMESTAMPS ARE RAW, DELIBERATELY. The server does not group these into
 * Today / This week / Earlier, because "today" is a question about the
 * VIEWER's timezone and the server does not know it — a server that guessed
 * would file a 23:30 conversation under "yesterday" for half its users. Group
 * them on the client, from `lastActivityAt`.
 *
 * `lastActivityAt` moves only when something was SAID in the thread. Renaming
 * or pinning an old conversation deliberately does not reorder it into today;
 * `updatedAt` is what moves then.
 */
export const threadSchema = z
    .object({
    id: z.string().uuid(),
    title: z.string().min(1),
    /** ISO instant when pinned, null when not. Pinned threads sort first. */
    pinnedAt: z.string().nullable(),
    /** ISO instant when archived, null when active. Archiving is reversible. */
    archivedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    lastActivityAt: z.string(),
    /** Opaque keyset boundary — hand it back as `cursor`, never parse it. */
    cursor: z.string(),
    /** Turn halves stored (a two-message exchange counts as 2). */
    messageCount: z.number().int().min(0).optional(),
})
    .strict();
/**
 * One turn half, as it was shown.
 *
 * `body` is the user-visible text and nothing else: what the person typed, or
 * the final answer they read. It is never an intermediate tool-calling
 * message, a raw tool result or a reasoning trace — the server has no column
 * for those and the append path has no parameter for them.
 *
 * The four structured fields ride ASSISTANT rows only (a database CHECK
 * enforces it), so a `user` row never carries them.
 */
export const threadMessageSchema = z
    .object({
    id: z.string().uuid(),
    role: z.enum(['user', 'assistant']),
    body: z.string().min(1),
    /** Dense, 0-based position in the thread; also the forward page cursor. */
    ordinal: z.number().int().min(0),
    createdAt: z.string(),
    /** The evidence manifest shown under the answer — parse with
     * `intelligence.ts`'s evidence schema to render it. References, not
     * retrieved content. */
    evidence: z.unknown().optional(),
    /** The typed result objects the answer was rendered with — parse with
     * `intelligence.ts`'s `intelligenceResultSchema`. */
    visualization: z.unknown().optional(),
    /** Opaque proposal references. They confer nothing: the owning domain
     * re-derives authority when the proposal is acted on. */
    dashboardProposalRef: z.string().optional(),
    automationProposalRef: z.string().optional(),
})
    .strict();
/**
 * A search snippet: text plus highlight OFFSETS into that text (UTF-16 code
 * units, exactly how every JS client slices strings).
 *
 * NEVER MARKUP. The server produces no HTML, so nothing said in a conversation
 * can become markup on somebody's screen; the client draws its own marks and
 * its own ellipses from `truncatedStart` / `truncatedEnd`.
 */
export const threadSnippetSchema = z
    .object({
    text: z.string(),
    truncatedStart: z.boolean(),
    truncatedEnd: z.boolean(),
    highlights: z.array(z.object({ start: z.number().int(), length: z.number().int() }).strict()),
})
    .strict();
export const threadSearchHitSchema = z
    .object({
    thread: threadSchema,
    /** The query matched the thread's title. */
    titleMatch: z.boolean(),
    /** Where in the transcript it matched — the EARLIEST matching message, so
     * the snippet shows where the topic started. Absent on a title-only hit. */
    message: z
        .object({
        id: z.string().uuid(),
        role: z.enum(['user', 'assistant']),
        ordinal: z.number().int().min(0),
        snippet: threadSnippetSchema,
    })
        .strict()
        .optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Response envelopes
// ---------------------------------------------------------------------------
const threadEnvelopeSchema = z.object({ thread: threadSchema, requestId: z.string() }).strict();
export const threadListResponseSchema = z
    .object({
    threads: z.array(threadSchema),
    hasMore: z.boolean(),
    /** Pass as `cursor` for the next page; null when there is none. */
    nextCursor: z.string().nullable(),
    requestId: z.string(),
})
    .strict();
export const threadDetailResponseSchema = z
    .object({
    thread: threadSchema,
    /** Oldest first — a conversation reads in the order it happened. */
    messages: z.array(threadMessageSchema),
    hasMore: z.boolean(),
    requestId: z.string(),
})
    .strict();
export const threadSearchResponseSchema = z
    .object({
    hits: z.array(threadSearchHitSchema),
    hasMore: z.boolean(),
    nextCursor: z.string().nullable(),
    requestId: z.string(),
})
    .strict();
export const threadDeleteResponseSchema = z
    .object({ deleted: z.literal(true), requestId: z.string() })
    .strict();
// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------
/**
 * Start a conversation.
 *
 * Supply `firstMessage` and the server derives the title from it —
 * deterministically, server-side, with no model call (a trimmed, capped
 * version of what was asked). Supply `title` to name it yourself. One of the
 * two is required: there is no placeholder title in this product, so a
 * conversation is never created as "New conversation".
 */
export const createThreadRequestSchema = z
    .object({
    firstMessage: z.string().trim().min(1).max(8_000).optional(),
    title: z.string().trim().min(1).max(THREAD_TITLE_MAX).optional(),
})
    .strict()
    .refine((body) => body.firstMessage !== undefined || body.title !== undefined, {
    message: 'A conversation needs a first message or a title.',
    path: ['firstMessage'],
});
/** Rename, pin/unpin, archive/unarchive. At least one field is required. */
export const updateThreadRequestSchema = z
    .object({
    title: z.string().trim().min(1).max(THREAD_TITLE_MAX).optional(),
    pinned: z.boolean().optional(),
    archived: z.boolean().optional(),
})
    .strict()
    .refine((body) => body.title !== undefined || body.pinned !== undefined || body.archived !== undefined, { message: 'Nothing to change.', path: ['title'] });
// ---------------------------------------------------------------------------
// The doors
// ---------------------------------------------------------------------------
/**
 * `POST /api/intelligence/threads` — start a conversation.
 *
 * Needs `intelligence:use`. The new thread is owned by the caller; there is no
 * way to create one into somebody else's list.
 */
export function create(body, options) {
    const parsed = createThreadRequestSchema.parse(body);
    return post('/api/intelligence/threads', parsed, options).then((response) => threadEnvelopeSchema.parse(response).thread);
}
/**
 * `GET /api/intelligence/threads` — the caller's OWN conversations.
 *
 * Needs `intelligence:use`. Pinned first, then most recent activity. Pass
 * `archived: true` for the archived list — the two are different screens, and
 * there is deliberately no combined list, because a mixed one has no honest
 * ordering between them.
 */
export function list(params = {}, options) {
    return get('/api/intelligence/threads', {
        ...options,
        query: {
            ...(params.archived !== undefined ? { archived: String(params.archived) } : {}),
            ...(params.cursor !== undefined ? { cursor: params.cursor } : {}),
            ...(params.limit !== undefined ? { limit: params.limit } : {}),
        },
    }).then((response) => threadListResponseSchema.parse(response));
}
/**
 * `GET /api/intelligence/threads/:threadId` — one conversation and a page of
 * its messages, oldest first.
 *
 * Needs `intelligence:use` AND ownership. A thread that is a colleague's
 * answers 404 with the identical body a thread that never existed gets — a
 * screen must present both as "not available" and must not infer existence
 * from anything.
 *
 * Pages forward on `afterOrdinal`: pass the last message's `ordinal`.
 */
export function get_(threadId, params = {}, options) {
    return get(`/api/intelligence/threads/${encodeURIComponent(threadId)}`, {
        ...options,
        query: {
            ...(params.afterOrdinal !== undefined ? { afterOrdinal: params.afterOrdinal } : {}),
            ...(params.limit !== undefined ? { limit: params.limit } : {}),
        },
    }).then((response) => threadDetailResponseSchema.parse(response));
}
export { get_ as detail };
/**
 * `PATCH /api/intelligence/threads/:threadId` — rename, pin/unpin,
 * archive/unarchive.
 *
 * Needs `intelligence:use` AND ownership; same collapsed 404 otherwise.
 * Pinning is personal: there is one owner, so there is one pin, and pinning
 * cannot affect what anybody else sees.
 */
export function update(threadId, body, options) {
    const parsed = updateThreadRequestSchema.parse(body);
    return patch(`/api/intelligence/threads/${encodeURIComponent(threadId)}`, parsed, options).then((response) => threadEnvelopeSchema.parse(response).thread);
}
/**
 * `DELETE /api/intelligence/threads/:threadId?confirm=true` — delete a
 * conversation FOR REAL.
 *
 * Needs `intelligence:use` AND ownership. This is a HARD delete: the thread
 * and every message in it are removed and there is nothing to undo. Use
 * `update(id, { archived: true })` for the reversible "get it out of my way".
 *
 * `confirm` is sent by this function and required by the server, so a delete
 * is never something a mistyped URL or a link prefetch performs — but a screen
 * should still ask the person first, because nothing downstream will.
 */
export function remove(threadId, options) {
    return del(`/api/intelligence/threads/${encodeURIComponent(threadId)}`, {
        ...options,
        query: { confirm: 'true' },
    }).then((response) => threadDeleteResponseSchema.parse(response));
}
/**
 * `GET /api/intelligence/threads/search` — search MY conversations, by title
 * and by transcript.
 *
 * Needs `intelligence:use`. Scoped to the caller: a query can never surface a
 * colleague's conversation — not its title, not a snippet, not the fact that
 * it exists. Archived conversations ARE searched, because archiving is "get
 * this out of my way", not "forget this happened".
 *
 * Word/prefix search, not substring: the final token matches as a prefix
 * ("morg" finds "Morgan"), and a mid-word substring does not match. Results
 * are most-recently-active first — plain recency, with no pinned-first rank,
 * because a search list answers "where did I say that".
 */
export function search(params, options) {
    return get('/api/intelligence/threads/search', {
        ...options,
        query: {
            q: params.q,
            ...(params.cursor !== undefined ? { cursor: params.cursor } : {}),
            ...(params.limit !== undefined ? { limit: params.limit } : {}),
        },
    }).then((response) => threadSearchResponseSchema.parse(response));
}
//# sourceMappingURL=intelligenceThreads.js.map